import React, { FunctionComponent, SyntheticEvent, useState } from 'react';
import { Button, Form } from '../ui';
import recipientInfo from '../../assets/images/recipients-info.svg';
import { Brief } from '../../services/models/brief';
import { BriefRecipient } from '../../services/models/brief-recipient';
import { VectorIcon } from '../VectorIcon';

export interface EditBriefRecipientsProps {
  brief: Brief;
  create: (email: string) => void;
  destroy: (id: number) => void;
  maxRecipients: number;
}

const EditBriefRecipients: FunctionComponent<EditBriefRecipientsProps> = ({
  brief,
  create,
  destroy,
  maxRecipients,
}) => {
  const [currentRecipient, setCurrentRecipient] = useState('');

  const addRecipient = (event: SyntheticEvent) => {
    event.preventDefault();
    if (isFull()) {
      return;
    }
    const value = currentRecipient.split(',')[0].trim().toLocaleLowerCase();
    if (isValid(value)) {
      if (
        !brief.recipients
          .filter(x => x && x.emailAddress)
          .some(x => x.emailAddress?.toLocaleLowerCase() === value)
      ) {
        create(value);
      }
      setCurrentRecipient('');
    }
  };

  const removeRecipient = (recipient: BriefRecipient) => {
    if (recipient && recipient.id) {
      destroy(recipient.id);
    }
  };

  const isValid = (value: string) => {
    const regex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(value);
  };

  const isFull = () => {
    return brief.recipients.length === maxRecipients;
  };

  const handleCurrentRecipientChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentRecipient(event.target.value);
  };

  const getTags = () => {
    const recipients = brief?.recipients || [];

    if (recipients.length === 0) {
      return null;
    }

    return (
      <div className="mt-5 flex w-full flex-col overflow-hidden rounded-md border border-gray-400">
        <div className="h-[45px] bg-primary px-2.5 text-left font-['Ubuntu'] text-[15px] font-bold tracking-[0.5px] leading-[45px] text-white">
          Email list
        </div>
        {recipients.map((x, i) => (
          <div
            className="group flex h-[45px] items-center justify-between border-t border-gray-300 bg-gray-100 px-2.5 text-primary"
            key={x.id ?? x.emailAddress ?? i}
          >
            <span>{x.emailAddress}</span>
            <Button
              variant="tool-button"
              className="transition-opacity pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100"
              onClick={() => removeRecipient(x)}
            >
              Delete
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const handleSeparator = (e: React.KeyboardEvent) => {
    const separators = [',', 'Enter', 'Tab'];

    if (separators.some(x => x === e.key)) {
      addRecipient(e);
    }
  };

  return (
    <div className="mx-auto mb-[30px] flex max-w-[260px] flex-col items-start justify-center">
      <div className="font-['Ubuntu'] text-[18px] font-bold tracking-[0.5px] text-black">
        Recipients
      </div>
      <div className="font-['Ubuntu'] text-[18px] font-light tracking-[0.5px] text-gray-700">
        Add recipients to share your News Brief.
      </div>
      <div className="mt-5 mb-5 flex w-full flex-col items-center justify-center">
        <img className="mx-auto my-[50px] block" src={recipientInfo} alt="Recipients Info" />
      </div>
      <Form noValidate className="w-full">
        <div className="flex">
          <Form.Control
            value={currentRecipient}
            disabled={isFull()}
            isInvalid={currentRecipient.length > 0 && !isValid(currentRecipient)}
            onChange={(e: any) => handleCurrentRecipientChange(e)}
            onKeyDownCapture={(e: any) => handleSeparator(e)}
            type="email"
            required
            className="rounded-r-none outline-hidden"
          />
          <Button
            disabled={!isValid(currentRecipient) && isFull()}
            variant="purple"
            className="rounded-l-none -ml-px shrink-0"
            onClick={(e: any) => addRecipient(e)}
          >
            <VectorIcon file="users_white" size={20} />
          </Button>
        </div>
        {getTags()}
      </Form>
    </div>
  );
};

export default EditBriefRecipients;
